// core/lib/analytics.ts — self-hosted Umami wiring (1.24.60).
//
// Umami is first-party (analytics.truxsocial.org, same box), cookieless, and
// collects no personal data — which is the only kind of analytics that sits
// honestly next to this platform's privacy posture. The Privacy Policy names
// it; keep that document in step with anything added here.
//
// Build-time configuration, mirroring the VITE_SUPPORT_URL pattern in
// config.ts: VITE_UMAMI_WEBSITE_ID unset/blank → the script is never injected
// and track() is a no-op, so dev, tests and any build without the id carry
// zero analytics code paths at runtime. The website id is minted by the Umami
// dashboard after first deploy and then baked into the prod build.
//
// Custom events are for the funnel steps a page view cannot see (page views
// themselves — including /join/<code> invite landings — Umami records on its
// own once the script is in):
//   signup-completed · email-verified · onboarding-finished · post-published
// "first post per visitor" is derived at analysis time from post-published;
// the client deliberately never decides what counts as someone's first.

declare global {
  interface Window {
    umami?: { track: (event: string, data?: Record<string, unknown>) => void };
  }
}

/** The Umami website id for this deployment, or null when analytics is off. */
export function getUmamiWebsiteId(): string | null {
  const raw = (import.meta.env.VITE_UMAMI_WEBSITE_ID as string | undefined) ?? '';
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Script URL — overridable for a future host move, defaults to the prod vhost. */
export function getUmamiSrc(): string {
  const raw = (import.meta.env.VITE_UMAMI_SRC as string | undefined) ?? '';
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : 'https://analytics.truxsocial.org/script.js';
}

/**
 * Inject the Umami script once, iff a website id is configured. Called from
 * main.tsx's boot; safe to call again (idempotent by data-website-id lookup).
 */
export function initAnalytics(doc: Document = document): void {
  const websiteId = getUmamiWebsiteId();
  if (!websiteId) return;
  if (doc.querySelector(`script[data-website-id="${websiteId}"]`)) return;
  const script = doc.createElement('script');
  script.defer = true;
  script.src = getUmamiSrc();
  script.setAttribute('data-website-id', websiteId);
  // 1.27.63: never record anything after "?". Query strings here carry live
  // one-time tokens (/reset-password?token=…, /verify-email?token=…), searches
  // (/explore?q=…) and ?next= targets. The tracker applies this to the page
  // address AND the referrer. The Privacy Policy says so; keep them in step.
  script.setAttribute('data-exclude-search', 'true');
  doc.head.appendChild(script);
}

/**
 * Record a funnel event. A deliberate no-op whenever the script is absent —
 * analytics must never be load-bearing: a blocked script, a dev build, or a
 * test run behaves identically to production minus the measurement.
 */
export function track(event: string, data?: Record<string, unknown>): void {
  try {
    window.umami?.track(event, data);
  } catch {
    // Swallow everything: a failed measurement must never surface to a member.
  }
}
